import React, { useRef, useEffect, useState } from "react";
import { VideoOff, RefreshCcw, User, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { captureAndSendScreenshot } from "./ExamUtils";
import { FaceNotDetectedDialog } from "./DialogModals";

const FaceDetectionHandler = ({
  sessionId,
  exam,
  examCandidateAPI,
  onFaceDetectionChange,
  faceDetectionEnabled,
}) => {
  const [cameraActive, setCameraActive] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [retryingCamera, setRetryingCamera] = useState(false);
  const [showFaceNotDetectedModal, setShowFaceNotDetectedModal] =
    useState(false);

    const [position, setPosition] = useState(() => {
  if (typeof window !== 'undefined') {
    return { 
      x: window.innerWidth - 20 - 192, // 12rem = 192px
      y: window.innerHeight - 20 - 144  // 9rem = 144px
    };
  }
  return { x: 20, y: 20 }; // Fallback
});
const [offset, setOffset] = useState({ x: 0, y: 0 });
const [dragging, setDragging] = useState(false);
const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const videoRef = useRef(null);
  const dragRef = useRef(null);
  const streamRef = useRef(null);
  const cameraRef = useRef(null);
  const faceDetectionRef = useRef(null);
  const faceBoundingBoxRef = useRef(null);
  const screenshotCanvasRef = useRef(document.createElement("canvas"));
  const lastScreenshotTimeRef = useRef(0);
  const mountedRef = useRef(true);
  const noFaceTimerRef = useRef(null);
  const { toast } = useToast();

  // Cleanup function to stop camera and clean up resources
  const cleanupResources = () => {
    
    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Stop camera instance
    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (e) {
        console.error("Error stopping camera:", e);
      }
      cameraRef.current = null;
    }

    // Clear face detection
    if (faceDetectionRef.current) {
      try {
        faceDetectionRef.current.close();
      } catch (e) {
        console.error("Error stopping face detection:", e);
      }
      faceDetectionRef.current = null;
    }

    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Clear timers
    if (noFaceTimerRef.current) {
      clearTimeout(noFaceTimerRef.current);
      noFaceTimerRef.current = null;
    }

    // Update states
    setCameraActive(false);
    setFaceDetected(false);
    faceBoundingBoxRef.current = null;
  };

  // Global cleanup function that can be called from anywhere
  useEffect(() => {
    // Expose cleanup function globally for emergency cleanup
    window.cleanupExamCamera = cleanupResources;
    
    return () => {
      // Remove global reference
      delete window.cleanupExamCamera;
    };
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cleanupResources();
    };
  }, []);

  // Load MediaPipe scripts dynamically
  const loadMediaPipeScripts = () => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.FaceDetection && window.Camera) {
        resolve({ FaceDetection: window.FaceDetection, Camera: window.Camera });
        return;
      }

      const loadScript = (src) => {
        return new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = src;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      };

      // Load MediaPipe scripts in sequence
      Promise.all([
        loadScript(
          "https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4.1646425229/face_detection.js"
        ),
        loadScript(
          "https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils@0.3.1640029074/camera_utils.js"
        ),
      ])
        .then(() => {
          // Wait a bit for the libraries to initialize
          setTimeout(() => {
            if (window.FaceDetection && window.Camera) {
              resolve({
                FaceDetection: window.FaceDetection,
                Camera: window.Camera,
              });
            } else {
              reject(new Error("MediaPipe libraries failed to load"));
            }
          }, 100);
        })
        .catch(reject);
    });
  };

  useEffect(() => {
  const handleResize = () => {
    if (!dragging) {
      setPosition({
        x: window.innerWidth - 20 - 192,
        y: window.innerHeight - 20 - 144
      });
    }
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, [dragging]);

  // Effect to handle face detection status changes
  useEffect(() => {
    // When face is detected, close the modal if it was open
    if (faceDetected && showFaceNotDetectedModal) {
      setShowFaceNotDetectedModal(false);

      // Clear any pending no-face timers
      if (noFaceTimerRef.current) {
        clearTimeout(noFaceTimerRef.current);
        noFaceTimerRef.current = null;
      }

      toast({
        title: "Face Detected",
        description: "You can now continue with your exam",
        variant: "success",
      });
    }
  }, [faceDetected, showFaceNotDetectedModal, toast]);

  // Initialize face detection
  useEffect(() => {
    if (!exam?.proctoring?.enabled || !exam?.proctoring?.faceDetection) return;

    const initFaceDetection = async () => {
      setInitializing(true);
      try {
        const { FaceDetection, Camera } = await loadMediaPipeScripts();

        const faceDetection = new FaceDetection({
          locateFile: (file) =>
            `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4.1646425229/${file}`,
        });

        faceDetection.setOptions({
          model: "short",
          minDetectionConfidence: 0.5,
        });

        faceDetection.onResults((results) => {
          if (!mountedRef.current) return;

          const hasFace = results.detections?.length > 0;

          // Update face detection state
          setFaceDetected(hasFace);
          onFaceDetectionChange(hasFace);

          // Handle face detection state
          if (hasFace) {
            // Save face bounding box
            if (results.detections[0]) {
              faceBoundingBoxRef.current = results.detections[0].boundingBox;
            }

            // Clear any pending no-face timers
            if (noFaceTimerRef.current) {
              clearTimeout(noFaceTimerRef.current);
              noFaceTimerRef.current = null;
            }
          } else if (!hasFace && !noFaceTimerRef.current) {
            // If no face is detected, start a timer to show the modal
            noFaceTimerRef.current = setTimeout(() => {
              if (!mountedRef.current) return;

              setShowFaceNotDetectedModal(true);

              // Send warning to the server
              sendProctoringWarning("Face not detected");
            }, 3000);
          }
        });

        await faceDetection.initialize();
        faceDetectionRef.current = faceDetection;

        if (videoRef.current) {
          const camera = new Camera(videoRef.current, {
            onFrame: async () => {
              if (
                faceDetectionRef.current &&
                videoRef.current?.readyState === 4
              ) {
                await faceDetectionRef.current.send({
                  image: videoRef.current,
                });
              }
            },
            width: 640,
            height: 480,
          });

          await camera.start();
          cameraRef.current = camera;
          setCameraActive(true);
        }
      } catch (error) {
        console.error("Error initializing face detection:", error);
        toast({
          title: "Camera Error",
          description: "Failed to initialize face detection",
          variant: "destructive",
        });
      } finally {
        setInitializing(false);
      }
    };

    initFaceDetection();

    return () => {
      mountedRef.current = false;
      cleanupResources();
    };
  }, [exam, toast, onFaceDetectionChange]);

  const sendProctoringWarning = async (message) => {
    try {
      if (examCandidateAPI && sessionId) {
        await examCandidateAPI.sendProctoringData(sessionId, {
          type: "warning",
          timestamp: new Date().toISOString(),
          details: message,
        });

        // Take a screenshot when warning is issued
        captureAndSendScreenshot(
          sessionId,
          examCandidateAPI,
          videoRef,
          screenshotCanvasRef,
          lastScreenshotTimeRef,
          cameraActive,
          exam,
          "warning"
        );
      }
    } catch (error) {
      console.error("Error sending proctoring warning:", error);
    }
  };

  const handleRetryCamera = async () => {
    if (retryingCamera) return;

    setRetryingCamera(true);
    try {
      // Use the cleanup function to properly stop everything
      cleanupResources();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setCameraActive(true);

      toast({
        title: "Camera Restarted",
        description: "Face detection system has been restarted",
      });
    } catch (error) {
      console.error("Error retrying camera:", error);
      toast({
        title: "Camera Error",
        description: "Failed to restart camera",
        variant: "destructive",
      });
    } finally {
      setRetryingCamera(false);
    }
  };

  const handleMouseDown = (e) => {
  setDragging(true);
  setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
};

useEffect(() => {
  if (!dragging) return;

  const handleMouseMove = (e) => {
    setPosition({
      x: e.clientX - startPos.x,
      y: e.clientY - startPos.y,
    });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  window.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", handleMouseUp);

  return () => {
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };
}, [dragging, startPos]);

  // Take periodic screenshots
  useEffect(() => {
    if (!cameraActive || !exam?.proctoring?.enabled) return;

    const intervalId = setInterval(() => {
      captureAndSendScreenshot(
        sessionId,
        examCandidateAPI,
        videoRef,
        screenshotCanvasRef,
        lastScreenshotTimeRef,
        cameraActive,
        exam,
        "periodic"
      );
    }, exam.proctoring.captureFrequency || 10000);

    return () => clearInterval(intervalId);
  }, [cameraActive, exam, sessionId, examCandidateAPI]);

  if (!faceDetectionEnabled) return null;

  return (
    <>
      {/* Warning banner */}
      {cameraActive &&
        !faceDetected &&
        !initializing &&
        !showFaceNotDetectedModal && (
          <div className="fixed top-0 left-0 right-0 bg-red-500 text-white p-2 z-50 flex items-center justify-center">
            <VideoOff className="h-5 w-5 mr-2" />
            <span>Face not visible! Please adjust your camera position.</span>
            <Button
              variant="outline"
              size="sm"
              className="ml-4 bg-white/10 hover:bg-white/20 text-white border-white/40"
              onClick={handleRetryCamera}
              disabled={retryingCamera}
            >
              <RefreshCcw className="h-3.5 w-3.5 mr-1" />
              {retryingCamera ? "Restarting..." : "Retry Camera"}
            </Button>
          </div>
        )}

      {/* Face Not Detected Modal */}
      <FaceNotDetectedDialog
        open={showFaceNotDetectedModal}
        onRetryCamera={handleRetryCamera}
        retryingCamera={retryingCamera}
      />

      {/* Camera preview */}
      <div
  ref={dragRef}
  onMouseDown={handleMouseDown}
  style={{
    position: "fixed",
    left: position.x,
    top: position.y,
    width: "12rem", // 48 w-48
    height: "9rem", // 36 h-36
    backgroundColor: "black",
    borderRadius: "0.375rem", // rounded-md
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    zIndex: 50,
    border: "2px solid var(--primary)",
    cursor: dragging ? "grabbing" : "grab"
  }}
>
  <video
    ref={videoRef}
    autoPlay
    playsInline
    muted
    className="w-full h-full object-cover"
  />
  <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-2 bg-black bg-opacity-50">
    <Badge
      variant={faceDetected ? "success" : "destructive"}
      className="text-xs"
    >
      {initializing
        ? "Initializing..."
        : faceDetected
        ? "Face Detected"
        : "No Face Detected"}
    </Badge>
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 text-white hover:text-primary hover:bg-black hover:bg-opacity-20"
      onClick={handleRetryCamera}
      disabled={retryingCamera || initializing}
    >
      <RefreshCcw
        className={`h-3 w-3 ${retryingCamera ? "animate-spin" : ""}`}
      />
    </Button>
  </div>

  {faceDetected && faceBoundingBoxRef.current && (
    <div
      style={{
        position: "absolute",
        top: `${
          faceBoundingBoxRef.current.yCenter * 100 -
          faceBoundingBoxRef.current.height * 50
        }%`,
        left: `${
          faceBoundingBoxRef.current.xCenter * 100 -
          faceBoundingBoxRef.current.width * 50
        }%`,
        width: `${faceBoundingBoxRef.current.width * 100}%`,
        height: `${faceBoundingBoxRef.current.height * 100}%`,
        border: "2px solid green",
        borderRadius: "4px",
      }}
    />
  )}
</div>
    </>
  );
};

export default FaceDetectionHandler;
